import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Loader2, Eye, Mail } from "lucide-react";

const TEMPLATES = [
  { kind: "confirmation",   label: "Subscription Confirmation",  tone: "emerald" },
  { kind: "receipt",        label: "Invoice Receipt",            tone: "slate" },
  { kind: "payment_failed", label: "Payment Failed",             tone: "red" },
  { kind: "trial_ending",   label: "Trial Ending",               tone: "amber" },
  { kind: "upgraded",       label: "Plan Upgraded",              tone: "emerald" },
  { kind: "downgraded",     label: "Plan Downgraded",            tone: "amber" },
  { kind: "canceled",       label: "Subscription Canceled",      tone: "slate" },
  { kind: "renewal",        label: "Renewal Reminder",           tone: "emerald" },
];

const TONE_HEX = {
  emerald: "#10B981",
  amber:   "#F59E0B",
  red:     "#DC2626",
  slate:   "#64748B",
};

export default function AdminEmailPreview() {
  const [active, setActive] = useState(TEMPLATES[0].kind);
  const [html, setHtml] = useState("");
  const [loading, setLoading] = useState(true);
  const iframeRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        // The endpoint returns text/html; use the underlying fetch via the api baseURL
        const base = api.defaults.baseURL || "";
        const token = api.defaults.headers?.common?.Authorization || "";
        const resp = await fetch(`${base}/billing/lifecycle-preview?kind=${active}`, {
          headers: token ? { Authorization: token } : {},
        });
        const text = await resp.text();
        if (!cancelled) setHtml(text);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [active]);

  useEffect(() => {
    if (!iframeRef.current) return;
    const doc = iframeRef.current.contentDocument;
    if (!doc) return;
    doc.open();
    doc.write(html);
    doc.close();
  }, [html]);

  return (
    <div className="grid md:grid-cols-[260px_1fr] gap-5" data-testid="admin-email-preview">
      {/* Template picker */}
      <div className="card p-3">
        <div className="text-[10px] uppercase tracking-widest text-[var(--ink-3)] font-semibold px-2 py-2">Lifecycle templates</div>
        <div className="space-y-1">
          {TEMPLATES.map((t) => {
            const tone = TONE_HEX[t.tone];
            const isActive = active === t.kind;
            return (
              <button
                key={t.kind}
                onClick={() => setActive(t.kind)}
                data-testid={`email-preview-pick-${t.kind}`}
                className={
                  "w-full text-left flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-all " +
                  (isActive
                    ? "bg-[var(--ink)] text-white"
                    : "text-[var(--ink-2)] hover:bg-[var(--bg)]")
                }
              >
                <span className="w-2 h-2 rounded-full" style={{ background: tone }} />
                <span className="font-semibold flex-1 truncate">{t.label}</span>
                {isActive && <Eye size={12} />}
              </button>
            );
          })}
        </div>
      </div>

      {/* Iframe preview */}
      <div className="card p-0 overflow-hidden relative">
        <div className="flex items-center gap-2 px-4 py-2.5 bg-[var(--bg)] border-b border-[var(--line)]">
          <Mail size={13} className="text-[var(--ink-3)]" />
          <span className="text-xs text-[var(--ink-2)] font-mono">{active}.html</span>
          {loading && <Loader2 size={12} className="animate-spin text-[var(--ink-3)] ml-auto" />}
        </div>
        <iframe
          ref={iframeRef}
          title="Email preview"
          className="w-full bg-white"
          style={{ minHeight: "780px", border: "none" }}
          data-testid="email-preview-iframe"
        />
      </div>
    </div>
  );
}
